<template>
  <f7-page :page-content="true" name="restricciones" @page:beforein="cargarLista">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('restricciones.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-button
          v-if="ficha && !error403"
          fill
          icon-f7="floppy_disk"
          :text="$t('restricciones.guardar')"
          class="display-flex"
          @click="guardar"
        ></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('restricciones.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <f7-block strong class="contadores no-margin-bottom">
        <span class="contador contador-verde">{{ $t('restricciones.configurados') }}: {{ contadores.configurados }}</span>
        <span class="contador contador-ambar">{{ $t('restricciones.aMedias') }}: {{ contadores.aMedias }}</span>
        <span class="contador contador-gris">{{ $t('restricciones.sinConfigurar') }}: {{ contadores.sinConfigurar }}</span>
        <span class="contador contador-total">{{ $t('restricciones.total') }}: {{ contadores.total }}</span>
      </f7-block>

      <!-- #376 · Censo por campo duro, DEBAJO de los contadores de estado y sin tocarlos: son los
           números que #358 pide publicar (base, radio y zona). Se pinta solo si la lista trae el
           desglose: contra una API anterior, `faltan` no viene y estos números serían falsos. -->
      <f7-block v-if="desgloseDisponible" strong class="contadores campos-duros no-margin-vertical">
        <span class="etiqueta-grupo">{{ $t('restricciones.camposDuros') }}:</span>
        <span class="contador contador-verde">{{ $t('restricciones.completas') }}: {{ contadores.completas }}</span>
        <span class="contador contador-rojo">{{ $t('restricciones.sinBase') }}: {{ contadores.sinBase }}</span>
        <span class="contador contador-rojo">{{ $t('restricciones.sinRadio') }}: {{ contadores.sinRadio }}</span>
        <span class="contador contador-rojo">{{ $t('restricciones.sinZona') }}: {{ contadores.sinZona }}</span>
        <span class="contador contador-total">{{ $t('restricciones.total') }}: {{ contadores.total }}</span>
        <div class="nota-campos-duros">{{ $t('restricciones.notaCamposDuros') }}</div>
      </f7-block>

      <!-- #774 · De dónde sale la base de cada inspector, contada sobre el mismo universo que las dos
           filas de arriba. Solo si la API la trae: contra una anterior no hay nada que contar. -->
      <f7-block v-if="basesDisponibles" strong class="contadores bases-resueltas no-margin-vertical">
        <span class="etiqueta-grupo">{{ $t('restricciones.baseResuelta.titulo') }}:</span>
        <span
          v-for="origen in ordenOrigenes"
          :key="origen"
          class="contador"
          :class="'contador-' + claseOrigen(origen)"
        >{{ $t('restricciones.baseResuelta.origen.' + origen) }}: {{ contadoresBase[origen] }}</span>
        <span v-if="contadoresBase.SIN_CALCULAR" class="contador contador-gris">{{ $t('restricciones.baseResuelta.sinCalcular') }}: {{ contadoresBase.SIN_CALCULAR }}</span>
        <span v-if="contadoresBase.DESACTUALIZADA" class="contador contador-gris">{{ $t('restricciones.baseResuelta.desactualizada') }}: {{ contadoresBase.DESACTUALIZADA }}</span>
        <f7-button
          v-if="puedeRecalcularBases"
          small
          outline
          icon-f7="arrow_clockwise"
          class="boton-recalcular-bases"
          :text="$t('restricciones.baseResuelta.recalcular')"
          @click="recalcularBases"
        ></f7-button>
        <div class="nota-campos-duros">{{ $t('restricciones.baseResuelta.nota') }}</div>
        <div v-if="contadoresBase.SIN_CALCULAR" class="nota-campos-duros">
          {{ puedeRecalcularBases
            ? $t('restricciones.baseResuelta.notaSinCalcular')
            : $t('restricciones.baseResuelta.notaSinCalcularSinPermiso') }}
        </div>
      </f7-block>

      <f7-row no-gap>
        <f7-col width="100" medium="30">
          <f7-block-title>{{ $t('restricciones.inspectores') }}</f7-block-title>
          <f7-list no-hairlines class="no-margin-vertical buscador-inspector">
            <f7-list-input
              type="text"
              :placeholder="$t('restricciones.buscarInspector')"
              :value="filtroInspector"
              clear-button
              @input="filtroInspector = ($event.target.value || '')"
              @input:clear="filtroInspector = ''"
            >
              <f7-icon slot="media" f7="search" size="18"></f7-icon>
            </f7-list-input>
            <!-- Toggle "Inspector activo": ON (defecto) = solo activos; OFF = todos. Se combina con la búsqueda. -->
            <f7-list-item class="toggle-activos">
              <span slot="title">Inspector activo</span>
              <f7-toggle slot="after" :checked="soloActivos" @toggle:change="soloActivos = $event"></f7-toggle>
            </f7-list-item>
          </f7-list>
          <f7-list no-hairlines class="lista-inspectores">
            <f7-list-item
              v-for="insp in inspectoresFiltrados"
              :key="insp.empleadoId"
              link="#"
              :class="{ 'inspector-seleccionado': insp.empleadoId == empleadoId, 'inspector-inactivo': insp.activo === false }"
              :title="insp.nombre + (insp.siglas ? ' (' + insp.siglas + ')' : '') + (insp.activo === false ? ' · INACTIVO' : '')"
              :footer="textoFaltan(insp)"
              @click.prevent="seleccionar(insp.empleadoId)"
            >
              <f7-icon slot="media" f7="circle_fill" size="14" :color="colorEstado(insp.estado)"></f7-icon>
              <f7-badge
                v-if="basesDisponibles"
                slot="after"
                class="badge-base"
                :class="'badge-' + claseBase(insp.base)"
              >{{ textoBaseCorto(insp.base) }}</f7-badge>
            </f7-list-item>
          </f7-list>
        </f7-col>

        <f7-col width="100" medium="70">
          <f7-block v-if="!ficha" strong>
            <p>{{ $t('restricciones.seleccioneInspector') }}</p>
          </f7-block>
          <r-form
            v-else
            ref="rform"
            :key="empleadoId"
            v-slot="{ rform }"
            :initialData="ficha"
          >
            <f7-card class="padding-vertical-half padding-right seccion-duras">
              <f7-card-header>{{ $t('restricciones.duras') }}</f7-card-header>
              <f7-card-content>
                <f7-row no-gap class="list list-form no-hairlines">
                  <f7-col width="100">
                    <div class="label-group">{{ $t('restricciones.baseGeo') }}</div>
                  </f7-col>
                  <!-- #774 · La base que el sistema ha resuelto para este inspector y de dónde sale.
                       Solo lectura: lo que se edita sigue siendo la base de salida de debajo. -->
                  <f7-col v-if="basesDisponibles" width="100">
                    <div class="base-resuelta" :class="'base-resuelta-' + claseBase(baseSeleccionada)">
                      <div>
                        <strong>{{ $t('restricciones.baseResuelta.titulo') }}: {{ textoBaseLargo(baseSeleccionada) }}</strong>
                        <span v-if="referenciaBase(baseSeleccionada)"> ({{ referenciaBase(baseSeleccionada) }})</span>
                        <span v-if="baseSeleccionada && baseSeleccionada.lat != null">
                          · {{ coordenada(baseSeleccionada.lat) }}, {{ coordenada(baseSeleccionada.lon) }}
                        </span>
                      </div>
                      <div v-if="baseSeleccionada && baseSeleccionada.textoPartida" class="base-resuelta-detalle">
                        {{ $t('restricciones.baseResuelta.desde') }} «{{ baseSeleccionada.textoPartida }}»
                        <span v-if="baseSeleccionada.fechaCalculo">
                          · {{ $t('restricciones.baseResuelta.calculada') }} {{ fechaCalculo(baseSeleccionada) }}
                        </span>
                      </div>
                      <div v-if="baseSeleccionada && baseSeleccionada.origen == 'SIN_BASE'" class="base-resuelta-detalle">
                        {{ $t('restricciones.baseResuelta.motivo.' + (baseSeleccionada.motivoSinBase || 'NO_RESUELVE')) }}
                        {{ $t('restricciones.baseResuelta.comoArreglar') }}
                      </div>
                      <div v-if="baseSeleccionada && baseSeleccionada.desactualizada" class="base-resuelta-detalle">
                        {{ $t('restricciones.baseResuelta.textoDesactualizada') }}
                      </div>
                      <div v-if="!baseSeleccionada" class="base-resuelta-detalle">
                        {{ puedeRecalcularBases
                          ? $t('restricciones.baseResuelta.notaSinCalcular')
                          : $t('restricciones.baseResuelta.notaSinCalcularSinPermiso') }}
                      </div>
                    </div>
                  </f7-col>
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.lat')" :form="rform" type="number" step="any" name="baseGeo.lat"></r-input>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.lon')" :form="rform" type="number" step="any" name="baseGeo.lon"></r-input>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="50">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.direccion')" :form="rform" type="text" name="baseGeo.direccion"></r-input>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input :label="$t('restricciones.pernoctaPermitida')" :form="rform" type="check" name="pernoctaPermitida"></r-input>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.maxNochesSeguidas')" :form="rform" type="number" name="maxNochesSeguidas"></r-input>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="50">
                    <ul>
                      <r-autocomplete-m2m
                        floating-label
                        outline
                        :label="$t('restricciones.diasSinPernocta')"
                        :form="rform"
                        name="diasSinPernocta"
                        :dbItems="diasSemana"
                        labelProp="nombre"
                        idProp="codigo"
                      ></r-autocomplete-m2m>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="50">
                    <ul>
                      <r-autocomplete-m2m
                        floating-label
                        outline
                        :label="$t('restricciones.zonasProvincias')"
                        :form="rform"
                        name="zonasProvincias"
                        :dbAdapter="Provincia"
                        labelProp="nombre"
                        idProp="codigo"
                        idFieldDetail="id"
                        type="textarea"
                        :lineBreak="true"
                      ></r-autocomplete-m2m>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.radioMaxKm')" :form="rform" type="number" name="radioMaxKm"></r-input>
                    </ul>
                    <!-- #689 · Guardián del radio: dentro de la columna del campo para no mover el resto de
                         la fila. Solo tras guardar, y la ficha ya está guardada con ese valor: avisa, no decide. -->
                    <div v-if="avisoRadio" class="aviso-radio">
                      <div class="aviso-radio-titulo">
                        <f7-icon f7="exclamationmark_triangle_fill" size="14"></f7-icon>
                        {{ $t('restricciones.avisoRadio.titulo', { valor: avisoRadio.radioMaxKm, umbral: avisoRadio.umbralKm }) }}
                      </div>
                      <div>{{ $t('restricciones.avisoRadio.porque') }}</div>
                      <div>{{ $t('restricciones.avisoRadio.quienYDonde') }}</div>
                    </div>
                  </f7-col>
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-autocomplete-m2m
                        floating-label
                        outline
                        :label="$t('restricciones.provinciasVetadas')"
                        :form="rform"
                        name="provinciasVetadas"
                        :dbAdapter="Provincia"
                        labelProp="nombre"
                        idProp="codigo"
                        idFieldDetail="id"
                        type="textarea"
                        :lineBreak="true"
                      ></r-autocomplete-m2m>
                    </ul>
                  </f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>

            <f7-card class="padding-vertical-half padding-right seccion-blandas">
              <f7-card-header>{{ $t('restricciones.blandas') }}</f7-card-header>
              <f7-card-content>
                <f7-row no-gap class="list list-form no-hairlines">
                  <f7-col width="100" medium="50">
                    <ul>
                      <r-autocomplete
                        floating-label
                        outline
                        :label="$t('restricciones.diaPreferidoInformes')"
                        :form="rform"
                        name="diaPreferidoInformes"
                        :dbItems="diasLaborables"
                        labelProp="nombre"
                        idProp="codigo"
                        :showNavigationLink="false"
                      ></r-autocomplete>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="50">
                    <ul>
                      <r-autocomplete-m2m
                        floating-label
                        outline
                        :label="$t('restricciones.zonaPreferente')"
                        :form="rform"
                        name="zonaPreferente"
                        :dbAdapter="Provincia"
                        labelProp="nombre"
                        idProp="codigo"
                        idFieldDetail="id"
                        type="textarea"
                        :lineBreak="true"
                      ></r-autocomplete-m2m>
                    </ul>
                  </f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>

            <f7-card class="padding-vertical-half padding-right">
              <f7-card-header>{{ $t('restricciones.jornada') }}</f7-card-header>
              <f7-card-content>
                <f7-row no-gap class="list list-form no-hairlines">
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.topeHorasDiaOverride')" :form="rform" type="number" name="topeHorasDiaOverride"></r-input>
                    </ul>
                  </f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>

            <f7-card class="padding-vertical-half padding-right seccion-notas">
              <f7-card-header>{{ $t('restricciones.notas') }}</f7-card-header>
              <f7-card-content>
                <f7-row no-gap class="list list-form no-hairlines">
                  <f7-col width="100">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.notas')" :form="rform" type="textarea" name="notas"></r-input>
                    </ul>
                  </f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>

            <f7-card class="padding-vertical-half padding-right">
              <f7-card-header>{{ $t('restricciones.acuerdo') }}</f7-card-header>
              <f7-card-content>
                <f7-row no-gap class="list list-form no-hairlines">
                  <f7-col width="100" medium="25">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.acuerdoFecha')" :form="rform" type="date" name="acuerdoFirmado.fecha"></r-input>
                    </ul>
                  </f7-col>
                  <f7-col width="100" medium="75">
                    <ul>
                      <r-input floating-label outline :label="$t('restricciones.acuerdoDocumento')" :form="rform" type="text" name="acuerdoFirmado.documento"></r-input>
                    </ul>
                  </f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>
          </r-form>
        </f7-col>
      </f7-row>
    </template>
  </f7-page>
</template>
<style scoped>
.contadores .contador {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  margin-right: 8px;
  color: #fff;
  font-weight: bold;
}
.contador-verde { background-color: #4cd964; }
.contador-ambar { background-color: #ff9500; }
.contador-gris { background-color: #8e8e93; }
.contador-total { background-color: #22355b; }
.contador-rojo { background-color: #d32f2f; }
.campos-duros .etiqueta-grupo {
  display: inline-block;
  margin-right: 8px;
  font-weight: bold;
  color: #555;
}
.nota-campos-duros {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #6d6d72;
}
/* Solo las filas con hueco traen pie; la ficha completa no dibuja ninguno. */
.lista-inspectores .item-footer {
  color: #d32f2f;
  font-weight: 600;
}
.toggle-activos .item-title { font-size: 13px; font-weight: 600; color: #555; }
.lista-inspectores .inspector-inactivo .item-title { color: #9aa1a8; font-style: italic; }
.lista-inspectores .inspector-seleccionado {
  background-color: rgba(34, 53, 91, 0.1);
}
.seccion-duras .card-header { color: #d32f2f; font-weight: bold; }
.seccion-blandas .card-header { color: #ff9500; font-weight: bold; }
.seccion-notas .card-header { color: #8e8e93; font-weight: bold; }
.aviso-radio {
  margin: 4px 16px 8px 16px;
  padding: 6px 8px;
  border-left: 3px solid #ff9500;
  background-color: rgba(255, 149, 0, 0.08);
  font-size: 12px;
  line-height: 1.4;
  color: #555;
}
.aviso-radio-titulo {
  font-weight: bold;
  color: #b36800;
}
.label-group {
  margin-top: 12px;
  margin-left: 20px;
  font-weight: bold;
}
/* #774 · Base resuelta: verde la exacta, ámbar la aproximada, rojo sin base, gris sin calcular. */
.boton-recalcular-bases {
  display: inline-flex;
  vertical-align: middle;
}
.lista-inspectores .badge-base { font-size: 11px; }
.badge-verde { background-color: #4cd964; }
.badge-ambar { background-color: #ff9500; }
.badge-rojo { background-color: #d32f2f; }
.badge-gris { background-color: #8e8e93; }
.base-resuelta {
  margin: 6px 20px 4px;
  padding: 8px 12px;
  border-left: 4px solid #8e8e93;
  background-color: rgba(0, 0, 0, 0.03);
  font-size: 13px;
  line-height: 1.4;
}
.base-resuelta-verde { border-left-color: #4cd964; }
.base-resuelta-ambar { border-left-color: #ff9500; }
.base-resuelta-rojo { border-left-color: #d32f2f; }
.base-resuelta-detalle { color: #6d6d72; margin-top: 2px; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import rAutocompleteM2m from "./../../components/rAutocompleteM2m.vue";
import Restriccion from "./../../js/db/Restriccion.js";
import Provincia from "./../../js/db/Provincia.js";
import Inspector from "./../../js/db/Inspector.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

export default {
  components: {
    rForm,
    rInput,
    rAutocomplete,
    rAutocompleteM2m
  },
  data: function() {
    return {
      Provincia,
      inspectores: [],
      filtroInspector: '',
      // Filtro "Inspector activo": por defecto SOLO activos (mismo criterio que el backend de siempre:
      // baja explícita del empleado = activo=false). Desmarcado → todos. Se combina con el buscador.
      soloActivos: true,
      empleadoId: null,
      ficha: null,
      // #689 · Aviso del último guardado de ESTA ficha ({radioMaxKm, umbralKm}) o null. No sale al
      // abrir una ficha: solo cuando una persona guarda.
      avisoRadio: null,
      error403: false,
      // #774 · Si la API trae la base resuelta, y si quien mira puede recalcularla para todos.
      basesDisponibles: false,
      puedeRecalcularBases: false,
      ordenOrigenes: ['FICHA_RESTRICCIONES', 'CP_EXACTO', 'CP_3_DIGITOS', 'POBLACION', 'OFICINA_APROXIMADA', 'SIN_BASE']
    };
  },
  computed: {
    diasSemana: function() {
      var self = this;
      return ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'].map(function(codigo) {
        return { codigo: codigo, nombre: self.$t('restricciones.dias.' + codigo) };
      });
    },
    diasLaborables: function() {
      return this.diasSemana.slice(0, 5);
    },
    // Universo según el toggle "Inspector activo" (la lista llega COMPLETA del backend con el flag
    // por inspector: una sola petición, el toggle filtra en memoria — instantáneo).
    inspectoresSegunActivo: function() {
      if (!this.soloActivos) return this.inspectores;
      return this.inspectores.filter(function(insp) { return insp.activo !== false; });
    },
    inspectoresFiltrados: function() {
      var q = this.normalizarTexto(this.filtroInspector);
      if (!q) return this.inspectoresSegunActivo;
      var self = this;
      return this.inspectoresSegunActivo.filter(function(insp) {
        var txt = self.normalizarTexto((insp.nombre || '') + ' ' + (insp.siglas || ''));
        return txt.indexOf(q) !== -1;
      });
    },
    // Contadores coherentes con el toggle (cuentan el universo visible, no lo que oculta el buscador).
    // Los del desglose (#376) salen del mismo recorrido y del mismo `faltan` que pinta cada fila:
    // la cabecera no puede decir un número distinto del que se cuenta mirando la lista.
    contadores: function() {
      const c = {
        configurados: 0, aMedias: 0, sinConfigurar: 0, total: 0,
        completas: 0, sinBase: 0, sinRadio: 0, sinZona: 0
      };
      this.inspectoresSegunActivo.forEach(function(insp) {
        if (insp.estado == 'COMPLETO') c.configurados++;
        else if (insp.estado == 'A_MEDIAS') c.aMedias++;
        else c.sinConfigurar++;
        c.total++;
        const faltan = insp.faltan || [];
        if (!faltan.length) c.completas++;
        // No son excluyentes: a quien le falten base y zona se le cuenta en las dos.
        if (faltan.indexOf('BASE') !== -1) c.sinBase++;
        if (faltan.indexOf('RADIO') !== -1) c.sinRadio++;
        if (faltan.indexOf('ZONA') !== -1) c.sinZona++;
      });
      return c;
    },
    // #774 · Mismo universo que los contadores de arriba (el del toggle) y la misma `base` que pinta
    // cada fila. Las desactualizadas cuentan en su origen y, además, aparte.
    contadoresBase: function() {
      const c = { SIN_CALCULAR: 0, DESACTUALIZADA: 0 };
      this.ordenOrigenes.forEach(function(origen) { c[origen] = 0; });
      this.inspectoresSegunActivo.forEach(function(insp) {
        if (!insp.base || !insp.base.origen) {
          c.SIN_CALCULAR++;
          return;
        }
        c[insp.base.origen] = (c[insp.base.origen] || 0) + 1;
        if (insp.base.desactualizada) c.DESACTUALIZADA++;
      });
      return c;
    },
    baseSeleccionada: function() {
      const empleadoId = this.empleadoId;
      const insp = this.inspectores.find(function(i) { return i.empleadoId == empleadoId; });
      return insp && insp.base ? insp.base : null;
    },
    // El desglose se pinta solo si TODA la lista lo trae: con una API anterior `faltan` no existe
    // y los contadores por campo dirían «0 sin base» de un censo que nadie ha medido.
    desgloseDisponible: function() {
      return this.inspectores.length > 0 && this.inspectores.every(function(insp) {
        return Array.isArray(insp.faltan);
      });
    }
  },
  methods: {
    // Qué le falta a esta ficha, por su nombre: «Falta: base · radio». Cadena vacía si no le falta
    // nada — así f7-list-item no dibuja el pie y la fila se ve exactamente como antes de #376.
    textoFaltan: function(insp) {
      const self = this;
      const faltan = (insp && insp.faltan) || [];
      if (!faltan.length) return '';
      return self.$t('restricciones.falta') + ': ' + faltan.map(function(campo) {
        return self.$t('restricciones.campoDuro.' + campo);
      }).join(' · ');
    },
    claseOrigen: function(origen) {
      if (origen == 'FICHA_RESTRICCIONES' || origen == 'CP_EXACTO') return 'verde';
      if (origen == 'SIN_BASE') return 'rojo';
      return 'ambar';
    },
    claseBase: function(base) {
      if (!base || !base.origen || base.desactualizada) return 'gris';
      return this.claseOrigen(base.origen);
    },
    textoBaseCorto: function(base) {
      if (!base || !base.origen) return this.$t('restricciones.baseResuelta.sinCalcular');
      if (base.desactualizada) return this.$t('restricciones.baseResuelta.desactualizada');
      return this.$t('restricciones.baseResuelta.corto.' + base.origen);
    },
    textoBaseLargo: function(base) {
      if (!base || !base.origen) return this.$t('restricciones.baseResuelta.sinCalcular');
      return this.$t('restricciones.baseResuelta.origen.' + base.origen);
    },
    referenciaBase: function(base) {
      if (!base || !base.referencia) return '';
      const clave = 'restricciones.baseResuelta.referencia.' + base.origen;
      if (!this.$te(clave)) return '';
      return this.$t(clave, { ref: base.referencia, n: base.cpsPromediados });
    },
    coordenada: function(valor) {
      return valor == null ? '' : Number(valor).toFixed(6);
    },
    fechaCalculo: function(base) {
      return base && base.fechaCalculo ? new Date(base.fechaCalculo).toLocaleString('es-ES') : '';
    },
    recalcularBases: function() {
      const self = this;
      const app = self.$f7;
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que la apagaba.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      Restriccion.recalcularBases()
        .then(function(res) {
          res = res || {};
          const porOrigen = res.porOrigen || {};
          const partes = self.ordenOrigenes
            .filter(function(origen) { return porOrigen[origen]; })
            .map(function(origen) {
              return self.$t('restricciones.baseResuelta.origen.' + origen) + ': ' + porOrigen[origen];
            });
          const texto = self.$t('restricciones.baseResuelta.recalculadas') + ': ' + (res.total || 0)
            + (partes.length ? ' (' + partes.join(' · ') + ')' : '');
          if (res.fallidos) {
            // Que uno falle no se calla: se dice cuántos y se ven como «Sin calcular» o «Desactualizada».
            app.dialog.alert(texto + '. ' + res.fallidos + ' ' + self.$t('restricciones.baseResuelta.fallidas') + '.');
          } else {
            app.toast.create({ text: texto, position: 'center', closeTimeout: 3500 }).open();
          }
          self.cargarLista();
        })
        .catch(function(e) {
          self.tratarError(e);
        })
        .then(function() {
          espera.cerrar();
        });
    },
    normalizarTexto: function(t) {
      return (t || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    },
    colorEstado: function(estado) {
      if (estado == 'COMPLETO') return 'green';
      if (estado == 'A_MEDIAS') return 'orange';
      return 'gray';
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    tratarError: function(e) {
      var self = this;
      var app = self.$f7;
      if (self.es403(e)) {
        self.error403 = true;
      } else {
        app.dialog.alert(e && e.message ? e.message : self.$t('searchBarSinResultados'));
      }
    },
    cargarLista: function() {
      var self = this;
      // incluirInactivos=true: se trae TODO una vez (con el flag activo por inspector); el toggle
      // y los contadores filtran en memoria. Los contadores pasan a ser computed (coherentes con el toggle).
      Restriccion.listarInspectores(true)
        .then(function(res) {
          res = res || {};
          self.inspectores = res.inspectores || [];
          // El flag siempre viaja (es un boolean): si no está, la API es anterior a la #774.
          self.basesDisponibles = typeof res.puedeRecalcularBases === 'boolean';
          self.puedeRecalcularBases = res.puedeRecalcularBases === true;
        })
        .catch(function(e) {
          self.tratarError(e);
        });
    },
    fichaVacia: function(empleadoId) {
      return {
        empleadoId: empleadoId,
        siglas: null,
        baseGeo: { lat: null, lon: null, direccion: null },
        pernoctaPermitida: false,
        maxNochesSeguidas: null,
        diasSinPernocta: [],
        zonasProvincias: [],
        radioMaxKm: null,
        provinciasVetadas: [],
        diaPreferidoInformes: null,
        zonaPreferente: [],
        topeHorasDiaOverride: null,
        notas: null,
        acuerdoFirmado: { fecha: null, documento: null }
      };
    },
    normalizarFicha: function(ficha, empleadoId) {
      // El upsert envía la ficha completa; los anidados y arrays deben existir
      // aunque el documento guardado no los traiga.
      var base = this.fichaVacia(empleadoId);
      ficha = Object.assign({}, base, ficha || {});
      ficha.baseGeo = Object.assign({}, base.baseGeo, ficha.baseGeo || {});
      ficha.acuerdoFirmado = Object.assign({}, base.acuerdoFirmado, ficha.acuerdoFirmado || {});
      ficha.diasSinPernocta = ficha.diasSinPernocta || [];
      ficha.zonasProvincias = ficha.zonasProvincias || [];
      ficha.provinciasVetadas = ficha.provinciasVetadas || [];
      ficha.zonaPreferente = ficha.zonaPreferente || [];
      return ficha;
    },
    seleccionar: function(empleadoId) {
      var self = this;
      var app = self.$f7;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      Restriccion.getFicha(empleadoId)
        .then(function(res) {
          self.empleadoId = empleadoId;
          self.avisoRadio = null;
          if (res) {
            self.ficha = self.normalizarFicha(res, empleadoId);
            return null;
          }
          // SIN_CONFIGURAR: ficha vacía con la dirección del inspector como
          // sugerencia de base de salida.
          self.ficha = self.fichaVacia(empleadoId);
          return Inspector.get({ itemId: empleadoId })
            .then(function(insp) {
              if (insp && self.empleadoId == empleadoId) {
                self.ficha.baseGeo.direccion = insp.direccionInicio || null;
              }
            })
            .catch(function() { /* la precarga es solo una sugerencia */ });
        })
        .catch(function(e) {
          self.tratarError(e);
        })
        .then(function() {
          espera.cerrar();
        });
    },
    guardar: function() {
      var self = this;
      var app = self.$f7;
      if (!self.$refs.rform || !self.empleadoId) return;
      var data = JSON.parse(JSON.stringify(self.$refs.rform.formData));
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      Restriccion.guardar(self.empleadoId, data)
        .then(function(res) {
          self.avisoRadio = (res && res.avisoRadio) || null;
          if (res) {
            // El aviso viaja junto a la ficha en la respuesta, pero no es de la ficha: fuera del
            // formulario, o volvería al servidor en el siguiente guardado.
            res = Object.assign({}, res);
            delete res.avisoRadio;
            self.ficha = self.normalizarFicha(res, self.empleadoId);
            if (self.$refs.rform) {
              self.$refs.rform.formData = self.ficha;
            }
          }
          app.toast
            .create({
              text: self.$t('restricciones.fichaGuardada'),
              position: 'center',
              closeTimeout: 2000,
              icon: '<i class="f7-icons">checkmark_alt</i>'
            })
            .open();
          self.cargarLista();
        })
        .catch(function(e) {
          self.tratarError(e);
        })
        .then(function() {
          espera.cerrar();
        });
    }
  }
};
</script>
